import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { formatMoney, formatPlacedDate, formatPlacedTime, ACTIVE_ORDER_STATUSES } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import {
  humanOrderStatus,
  formatOrderTitleSummary,
  lineItemCount,
  orderDisplayNumber,
  orderRowId,
  pickAppUserId,
  pickCustomerEmail,
  pickCustomerName,
  pickCustomerPhone,
  pickDeliveryContactPhone,
  pickDeliveryRecipientName,
  pickPaymentSummary,
  pickTotalAmount,
  statusSelectOptions,
  isPickupOrder,
} from '../lib/orders';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
// import { FilterSubmitButton } from '../components/FilterSubmitButton';
import { ConfirmDialog } from '../components/Modal';
import { useAdminRealtime } from '../context/AdminRealtimeContext';

export default function Orders() {
  const { subscribeOrderPlaced } = useAdminRealtime();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [orderType, setOrderType] = useState('');
  const [orderNumberContains, setOrderNumberContains] = useState('');
  const [busyId, setBusyId] = useState('');
  const [pendingCancel, setPendingCancel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (status) params.status = status;
      if (orderType) params.orderType = orderType;
      if (orderNumberContains.trim()) params.orderNumberContains = orderNumberContains.trim();
      const raw = await adminApi.listActiveOrders(params);
      const norm = normalizeListResponse(raw);
      setRows(norm.items);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [status, orderType, orderNumberContains]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return subscribeOrderPlaced(() => {
      load();
    });
  }, [subscribeOrderPlaced, load]);

  async function applyRowStatus(row, nextStatus) {
    const id = orderRowId(row);
    if (!id || String(row.status).toUpperCase() === String(nextStatus).toUpperCase()) return;
    if (nextStatus === 'CANCELLED') {
      setPendingCancel({ id, row, nextStatus });
      return;
    }
    setBusyId(id);
    setError('');
    try {
      await adminApi.patchOrderStatus(id, { status: nextStatus });
      await load();
    } catch (e) {
      setError(pickErrorMessage(e));
      await load();
    } finally {
      setBusyId('');
    }
  }

  async function confirmCancel() {
    if (!pendingCancel) return;
    const { id, nextStatus } = pendingCancel;
    setBusyId(id);
    setError('');
    try {
      await adminApi.patchOrderStatus(id, { status: nextStatus });
      await load();
      setPendingCancel(null);
    } catch (e) {
      setError(pickErrorMessage(e));
      await load();
    } finally {
      setBusyId('');
    }
  }

  const columns = [
    {
      key: 'orderNumber',
      label: 'Order',
      width: '12rem',
      render: (r) => {
        const id = orderRowId(r);
        const no = orderDisplayNumber(r);
        const title = formatOrderTitleSummary(r);
        return (
          <div>
            <Link to={id ? `/orders/${id}` : '#'} className="fw-semibold text-decoration-none">
              {no}
            </Link>
            {title ? (
              <div className="small text-secondary order-items-summary mt-1">{title}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      width: '7.5rem',
      render: (r) => {
        const isPickup = isPickupOrder(r);
        return isPickup ? (
          <span className="badge text-bg-warning text-dark">🏬 Pickup</span>
        ) : (
          <span className="badge text-bg-light border text-secondary">🛵 Delivery</span>
        );
      },
    },
    {
      key: 'placed',
      label: 'Placed',
      width: '11rem',
      render: (r) => {
        const iso = r.placedAt || r.createdAt || r.created_at || r.orderedAt;
        return (
          <div className="small">
            <div className="text-dark text-nowrap">{formatPlacedDate(iso)}</div>
            <div className="text-secondary order-placed-time text-nowrap">{formatPlacedTime(iso)}</div>
          </div>
        );
      },
    },
    {
      key: 'customer',
      label: 'Customer',
      width: '11rem',
      render: (r) => {
        const custName = pickCustomerName(r);
        const recipient = pickDeliveryRecipientName(r);
        const name = custName && custName.toLowerCase() !== 'user' ? custName : (recipient || custName);
        const uid = pickAppUserId(r);
        return (
          <div className="small">
            {name ? <div className="fw-medium text-dark text-truncate" title={name}>{name}</div> : <span className="text-secondary">—</span>}
            {uid ? (
              <Link to={`/app-users/${uid}`} className="text-decoration-none" onClick={(e) => e.stopPropagation()}>
                User
              </Link>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'contact',
      label: 'Contact',
      width: '12rem',
      render: (r) => {
        const em = pickCustomerEmail(r);
        const ph = pickDeliveryContactPhone(r) || pickCustomerPhone(r);
        return (
          <div className="small text-secondary">
            {em ? (
              <div className="text-truncate" title={em}>
                {em}
              </div>
            ) : null}
            {ph ? <div className="text-truncate">{ph}</div> : null}
            {!em && !ph ? '—' : null}
          </div>
        );
      },
    },
    {
      key: 'items',
      label: 'Items',
      width: '4rem',
      render: (r) => {
        const n = lineItemCount(r);
        return n != null ? <span className="tabular-nums">{n}</span> : <span className="text-secondary">—</span>;
      },
    },
    {
      key: 'total',
      label: 'Total',
      width: '7rem',
      render: (r) => <span className="tabular-nums fw-medium">{formatMoney(pickTotalAmount(r))}</span>,
    },
    {
      key: 'payment',
      label: 'Pay',
      width: '7rem',
      render: (r) => {
        const { method, status: ps } = pickPaymentSummary(r);
        const bit = [method, ps].filter(Boolean).join(' · ');
        return bit ? <span className="small text-secondary text-truncate d-block" title={bit}>{bit}</span> : '—';
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '12.5rem',
      render: (r) => {
        const id = orderRowId(r);
        const opts = statusSelectOptions(r.status, r);
        const cur = String(r.status || '').toUpperCase();
        const terminal = cur === 'REFUNDED' || cur === 'CANCELLED';
        const disabled = !id || busyId === id || terminal;
        return (
          <div className="d-flex flex-column gap-1 align-items-start">
            <select
              className="form-select form-select-sm"
              aria-label={`Status for order ${orderDisplayNumber(r)}`}
              value={String(r.status || '').toUpperCase()}
              disabled={disabled}
              onChange={(e) => applyRowStatus(r, e.target.value)}
            >
              {opts.map((s) => (
                <option key={s} value={s}>
                  {humanOrderStatus(s)}
                </option>
              ))}
            </select>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '5.5rem',
      render: (r) => {
        const id = orderRowId(r);
        return id ? (
          <Link to={`/orders/${id}`} className="btn btn-sm btn-outline-primary">
            Details
          </Link>
        ) : (
          '—'
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Orders" subtitle="Active fulfillment queue — in-progress orders only" />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-md-3 col-lg-2">
              <label className="form-label small mb-1">Status</label>
              <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All active</option>
                {ACTIVE_ORDER_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {humanOrderStatus(s)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3 col-lg-2">
              <label className="form-label small mb-1">Type</label>
              <select className="form-select" value={orderType} onChange={(e) => setOrderType(e.target.value)}>
                <option value="">All types</option>
                <option value="DELIVERY">🛵 Delivery</option>
                <option value="PICKUP">🏬 Self Pickup</option>
              </select>
            </div>
            <div className="col-md-6 col-lg-4">
              <label className="form-label small mb-1">Order # contains</label>
              <input
                className="form-control"
                value={orderNumberContains}
                onChange={(e) => setOrderNumberContains(e.target.value)}
                placeholder="Search number…"
              />
            </div>
          </div>
        </CardBody>
      </Card>
      <Card className="admin-orders-table-card">
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status" aria-label="Loading" />
            </div>
          ) : (
            <div className="admin-orders-scroll-boundary">
              <DataTable
                columns={columns}
                rows={rows}
                emptyText="No active orders match these filters."
                rowKey={(r, i) => orderRowId(r) || i}
              />
            </div>
          )}
        </CardBody>
      </Card>

      {pendingCancel ? (
        <ConfirmDialog
          title="Cancel order?"
          message={`Set order ${orderDisplayNumber(pendingCancel.row)} to Cancelled? Customers are usually notified automatically.`}
          confirmLabel="Cancel order"
          danger
          busy={busyId === pendingCancel.id}
          onCancel={() => {
            if (busyId) return;
            setPendingCancel(null);
          }}
          onConfirm={confirmCancel}
        />
      ) : null}
    </>
  );
}
